<template>
  <div>
    <div v-if="!loading">
      <new-main-header v-if="hasNewLeftNavEnabled">
        {{ pageTitle }}
      </new-main-header>
      <main-header v-else>
        <span slot="arrow-back">
          <el-tooltip
              class="item"
              effect="dark"
              content="Back to Devices"
              placement="bottom-start">
            <router-link :to="{ name: 'Devices', params: { id: $route.params.id } }"><i class="el-icon-back" /> </router-link>
          </el-tooltip>
        </span>
        <span
            slot="page-title"
            v-loading="!pageTitle">{{ pageTitle }}</span>
      </main-header>
      <div v-if="meta.device.found">
        <main-content>
          <div slot="main-content">
            <div v-if="loading"></div>
            <div v-else-if="doorbotId">
              <Troubleshooting :data="dynamicTags" />
              <device-general
                  id="device-general"
                  :loading="loading"
                  :device="device"
                  :last-status-report="lastStatusReport" />
              <device-firmware
                  v-if="showSection('firmwares') && !isBaseStation"
                  id="device-firmwares"
                  :device="device" />
              <schedules
                  v-if="isDeviceWithScheduleSupport"
                  :device-name="deviceName" />
              <old-schedules
                  v-else-if="isDeviceWithScheduleSupport"
                  :device-name="deviceName" />
              <device-settings
                  v-if="showSection('device-settings') && !isBaseStation"
                  id="device-settings" />
              <device-links id="device-links" />
              <linked-devices
                  v-if="isChime"
                  :chime-id="doorbotId" />
              <events
                  v-if="isNotChime && showSection('events')"
                  id="device-events"
                  :by-device="true"
                  :device-type="deviceType" />
              <device-status-reports
                  v-if="showSection('reports-summary')"
                  id="device-status-reports"
                  :device="device" />
              <device-setups
                  v-if="showSection('setups')"
                  id="device-setups"
                  :show-device-name="false"
                  :entity="{ model: 'devices', id: doorbotId }" />
            </div>
          </div>
        </main-content>
      </div>
    </div>
    <div v-else>
      <div style="font-size: 2rem; text-align: center; margin: 20px auto; width: 50%; color: #1998d5;">
        <p>
          <font-awesome-icon
              icon="circle-notch"
              spin />
          Loading device...
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import MainContent from "@/containers/Core/MainContent";
import MainHeader from "@/containers/Core/Header/MainHeader";
import DeviceGeneral from "@/containers/Customer/Devices/DeviceGeneral";
import DeviceFirmware from "@/containers/Customer/Devices/DeviceFirmware";
import DeviceSettings from "@/containers/Customer/Devices/DeviceSettings";
import Events from "@/containers/Customer/History/Events";
import DeviceLinks from "@/containers/Customer/Devices/DeviceLinks";
import DeviceStatusReports from "@/containers/Customer/Devices/StatusReports/DeviceStatusReports";
import DeviceSetups from "@/containers/Customer/Devices/Setups/DeviceSetups";
import NotesTags from "@/containers/Customer/Devices/NotesTags";
import Tags from "@/components/Shared/Tags";
import Troubleshooting from "@/containers/Customer/Devices/Troubleshooting";
import Schedules from "@/NewUI/containers/customer/devices/schedules/Schedules.vue";
import OldSchedules from "@/containers/Customer/Devices/Schedules/Schedules.vue";
import NewMainHeader from "@/NewUI/containers/core/header/MainHeader";

import store from "@/modules";
import { get, find } from "lodash";
import permission from "@/mixins/permission";

import { createNamespacedHelpers } from "vuex";
import LinkedDevices from "@/containers/Customer/Devices/LinkedDevices/LinkedDevices";
const { mapActions, mapGetters, mapMutations } =
  createNamespacedHelpers("db/device");
const { mapGetters: sharedGetters, mapMutations: sharedMutations } =
  createNamespacedHelpers("shared");
const { mapActions: uiActions, mapGetters: uiGetters } =
  createNamespacedHelpers("ui");
const { mapGetters: authGetters } = createNamespacedHelpers("auth");

const shouldLoad = (to) => {
  let { email, doorbot_id } = to.params;
  let stored_email = store.getters["db/account/email"];
  let stored_doorbot_id = store.getters["db/device/doorbotId"];
  const shouldNotLoad =
    Boolean(stored_email) &&
    Boolean(stored_doorbot_id) &&
    stored_email === email &&
    stored_doorbot_id === doorbot_id;
  return !shouldNotLoad;
};

const DEVICES_WITH_SCHEDULES = ["intercom_handset_audio"];

export default {
  name: "DeviceDetails",
  components: {
    LinkedDevices,
    MainContent,
    MainHeader,
    DeviceGeneral,
    DeviceFirmware,
    DeviceSettings,
    Events,
    DeviceLinks,
    DeviceStatusReports,
    DeviceSetups,
    NotesTags,
    Tags,
    Troubleshooting,
    Schedules,
    OldSchedules,
    NewMainHeader,
  },
  mixins: [permission],
  data() {
    return {
      loading: false,
      loaded: false,
      shouldLoad: null,
      routeFrom: {},
      schemaError: false,
      meta: {
        device: {
          found: null,
        },
      },
    };
  },
  computed: {
    ...mapGetters([
      "device",
      "doorbotId",
      "macId",
      "lastStatusReport",
      "deviceOptions",
    ]),
    ...sharedGetters(["products"]),
    ...uiGetters(["isLoading"]),
    ...authGetters(["user"]),
    hasNewLeftNavEnabled() {
      return this.hasPermission("NewUI", "leftNav");
    },
    dynamicTags() {
      return get(this.device, "troubleshooting", []);
    },
    isChime() {
      return get(this.device, "type", "").match(/^chime_?/);
    },
    isNotChime() {
      return !this.isChime;
    },
    customerId() {
      return this.device.owner_id;
    },
    product() {
      return (
        this.products.find((p) => {
          return p.kind === this.device.type;
        }) || {}
      );
    },
    anyProduct() {
      return this.products.length > 0;
    },
    pageTitle() {
      if (this.device.name) {
        return `${this.device.name} - ${this.product.description}`;
      } else {
        return null;
      }
    },
    isTrackerDevice() {
      return /vehicle_brody_v1|tracker_buddy/.test(this.device.type);
    },
    deviceType() {
      return get(this.device, "type", "");
    },
    isDeviceWithScheduleSupport() {
      return DEVICES_WITH_SCHEDULES.includes(this.deviceType);
    },
    deviceName() {
      return get(this.device, "name", "");
    },
    isBaseStation() {
      return /base_station|beams_bridge/.test(this.device.type);
    },
  },
  created() {
    this.loadDevice();
  },
  destroyed() {
    this.resetState();
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.shouldLoad = shouldLoad(to);
    });
  },
  beforeRouteUpdate(to, from, next) {
    this.shouldLoad = shouldLoad(to);
    next();
  },
  beforeRouteLeave(to, from, next) {
    this.resetState();
    next();
  },
  provide: {
    deviceName: "deviceName",
  },
  methods: {
    ...mapActions([
      "loadLastStatusReport",
      "getDeviceStatus",
      "fetchDeviceDetailed",
    ]),
    ...mapMutations(["resetState"]),
    ...uiActions(["setLoading"]),
    ...sharedMutations(["sendMessage"]),
    loadDevice() {
      const routeParamCustomerId = this.$route.params.customer_id;
      const doorbotId = this.$route.params.doorbot_id;
      const params = {
        id: doorbotId,
        includes: [
          "owner",
          "troubleshooting",
          "connection_status",
          "last_status_report",
          "authorized_users",
          "last_setup",
          "last_requests",
          "is_latest_owner",
          "profile",
        ],
      };
      this.loading = true;
      this.fetchDeviceDetailed(params)
        .then((response) => {
          try {
            if (response.meta.full_data) {
              this.meta.device.found = true;
              if (this.showSection("reports-summary")) {
                this.getLastStatusReport();
              }
            } else if (response.meta.profile_only) {
              this.sendMessage({ id: "device/profile", msg: response.profile });
              this.$router.push({
                name: "HomePage",
                props: { profile: response.device },
              });
            } else if (response.meta.no_data) {
              console.log("No data", response);
            } else if (!response.meta.full_data) {
              this.openNoSupportedDeviceMessage(response);
            }
          } catch (e) {
            this.setLoading(false);
            this.loading = false;
            console.error(e);
          }
        })
        .catch((error) => {
          this.$message({
            type: "error",
            message: "Device profile does not exits in admin",
          });
          this.$router.replace({ name: "HomePage" });
        })
        .finally(() => {
          this.setLoading(false);
          this.loading = false;
        });
    },
    showSection(sectionName) {
      const deviceSections = (this.deviceOptions.ui_sections || []).map(
        (section) => section.name
      );
      return deviceSections.includes(sectionName);
    },
    switchToStatic(data) {
      this.$message.error(data.message);
      this.schemaError = true;
    },
    getLastStatusReport() {
      this.loadLastStatusReport({ doorbotId: this.device.doorbot_id })
        .then(this.onSuccess)
        .catch((error) => {
          console.log(error);
        })
        .finally(this.getDeviceStatus);
    },
    onSuccess(msg) {
      return () => {
        this.$message({
          showClose: true,
          message: msg,
          type: "success",
        });
      };
    },
    onError(msg) {
      return (err) => {
        this.$message({
          showClose: true,
          message: msg,
          type: "error",
        });
      };
    },
    openNoSupportedDeviceMessage(resp) {
      let errorMsg = find(resp.device.admin_metadata, function (e) {
        return e.error.message.match(/Invalid device kind/);
      });
      if (errorMsg) {
        this.$alert(errorMsg.error.message, "Device not supported", {
          confirmButtonText: "OK",
          callback: () => {
            this.$router.push(`/customer/${this.customerId}/devices`);
          },
        });
      }
    },
  },
};
</script>

<style lang="scss">
.dynamic-tags__section {
  margin-bottom: 40px;
  .note {
    p {
      color: transparentize($--color-milano-red, 0.2);
    }
    .foot-note {
      div {
        color: $--color-dusty-gray;
      }
      i {
        cursor: pointer;
      }
    }
    .add-btn {
      margin: 18px 0;
    }
  }
  .tags {
    span.el-tag {
      margin-right: 20px;
      margin-bottom: 7px;
      height: 44px;
      border-radius: 20px;
      padding: 7px 10px;
      background: transparentize($--color-milano-red, 0.2);
      color: white;
    }
    .add-btn {
      margin: 18px 0;
    }
  }
}
</style>
